import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  Switch,
  FormControlLabel,
  Divider,
  Tabs,
  Tab,
  TextField,
  Paper,
  SxProps,
  Theme,
} from '@mui/material';
import { Settings as SettingsIcon } from '@mui/icons-material';
import { ProductLogo } from '@qwickapps/react-framework';
import { MustacheTemplateProvider } from '@qwickapps/schema';
import { useDocument } from '../contexts/DocumentContext';
import { TYPOGRAPHY_TEMPLATES, FONT_FAMILY_OPTIONS } from '../data/typographyTemplates';
import { createDefaultIcon } from '../data/defaultLetterhead';

export const TypographySettings: React.FC<{ sx?: SxProps<Theme> }> = ({ sx }) => {
  const [open, setOpen] = useState(false);
  const [tabIndex, setTabIndex] = useState(0);
  const { typographyStyle, setTypographyStyle, updateTypographyStyle, letterheadConfig, setLetterheadConfig } = useDocument();
  const templateProvider = useMemo(() => new MustacheTemplateProvider(), []);

  const handleTemplateChange = (templateId: string) => {
    const template = TYPOGRAPHY_TEMPLATES.find((t) => t.id === templateId);
    if (template) {
      setTypographyStyle(template);
    }
  };

  const handleReset = () => {
    const currentTemplate = TYPOGRAPHY_TEMPLATES.find((t) => t.id === typographyStyle.id);
    if (currentTemplate) {
      setTypographyStyle(currentTemplate);
    }
  };

  const handleLetterheadChange = (field: string, value: any) => {
    setLetterheadConfig({ [field]: value });
  };

  const renderTemplatePreview = (template: string | undefined) => {
    if (!template) return null;
    const hasLogoPlaceholder = template.includes('{{logo}}');
    const templateContext = {
      companyName: letterheadConfig.companyName,
      tagline: letterheadConfig.tagline,
      address: letterheadConfig.address,
      email: letterheadConfig.email,
      phone: letterheadConfig.phone,
      website: letterheadConfig.website,
      date: new Date().toLocaleDateString(),
      logo: hasLogoPlaceholder ? '<span data-logo-placeholder="true"></span>' : '',
    };

    try {
      const renderedHtml = templateProvider.resolve(template, templateContext);
      if (hasLogoPlaceholder) {
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
            {renderedHtml.split('<span data-logo-placeholder="true"></span>').map((part, index, arr) => (
              <React.Fragment key={index}>
                {part && <Box dangerouslySetInnerHTML={{ __html: part }} />}
                {index < arr.length - 1 && (
                  <ProductLogo
                    name={letterheadConfig.logoText || letterheadConfig.companyName}
                    size={letterheadConfig.logoSize}
                    variant={letterheadConfig.logoVariant}
                    icon={
                      letterheadConfig.logoUrl
                        ? React.createElement('img', {
                            src: letterheadConfig.logoUrl,
                            alt: letterheadConfig.companyName,
                            style: { display: 'block', height: '32px', width: '32px' },
                          })
                        : createDefaultIcon()
                    }
                  />
                )}
              </React.Fragment>
            ))}
          </Box>
        );
      }
      return <Box dangerouslySetInnerHTML={{ __html: renderedHtml }} />;
    } catch (error) {
      return (
        <Typography color="error" variant="caption">
          Template error: {error instanceof Error ? error.message : 'Invalid template'}
        </Typography>
      );
    }
  };

  return (
    <>
      <Button
        variant="text"
        size="small"
        startIcon={<SettingsIcon />}
        onClick={() => setOpen(true)}
        sx={{ textTransform: 'none', ...sx }}
      >
        Settings
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Settings</DialogTitle>

        <Tabs value={tabIndex} onChange={(_, v) => setTabIndex(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
          <Tab label="TYPOGRAPHY" />
          <Tab label="LETTERHEAD" />
        </Tabs>

        <DialogContent>
          {/* Typography Tab */}
          {tabIndex === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>

            {/* Typography Template Selector */}
            <Box>
              <FormControl fullWidth size="small">
                <InputLabel>Template</InputLabel>
                <Select
                  value={typographyStyle.id}
                  label="Template"
                  onChange={(e) => handleTemplateChange(e.target.value)}
                >
                  {TYPOGRAPHY_TEMPLATES.map((template) => (
                    <MenuItem key={template.id} value={template.id}>
                      <Box>
                        <Typography variant="body2">{template.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {template.description}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Divider />

            {/* Customization Controls */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="body2" fontWeight="600">
                  Customize
                </Typography>
                <Button size="small" onClick={handleReset} variant="outlined">
                  Reset
                </Button>
              </Box>

              {/* Font Family */}
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Font Family</InputLabel>
                <Select
                  value={typographyStyle.fontFamily}
                  label="Font Family"
                  onChange={(e) => updateTypographyStyle({ fontFamily: e.target.value })}
                >
                  {FONT_FAMILY_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Font Size */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Font Size: {typographyStyle.fontSize}px
                </Typography>
                <Slider
                  value={typographyStyle.fontSize}
                  onChange={(_, value) => updateTypographyStyle({ fontSize: value as number })}
                  min={10}
                  max={24}
                  step={1}
                  marks
                  size="small"
                />
              </Box>

              {/* Line Height */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Line Height: {typographyStyle.lineHeight.toFixed(1)}
                </Typography>
                <Slider
                  value={typographyStyle.lineHeight}
                  onChange={(_, value) => updateTypographyStyle({ lineHeight: value as number })}
                  min={1.0}
                  max={2.5}
                  step={0.1}
                  size="small"
                />
              </Box>

              {/* Paragraph Spacing */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Paragraph Spacing: {typographyStyle.paragraphSpacing.toFixed(1)}em
                </Typography>
                <Slider
                  value={typographyStyle.paragraphSpacing}
                  onChange={(_, value) => updateTypographyStyle({ paragraphSpacing: value as number })}
                  min={0.5}
                  max={3.0}
                  step={0.1}
                  size="small"
                />
              </Box>

              {/* Heading Scale */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Heading Scale: {typographyStyle.headingScale.toFixed(1)}x
                </Typography>
                <Slider
                  value={typographyStyle.headingScale}
                  onChange={(_, value) => updateTypographyStyle({ headingScale: value as number })}
                  min={1.0}
                  max={2.0}
                  step={0.1}
                  size="small"
                />
              </Box>

              {/* Heading Top Margin */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Heading Top Margin: {typographyStyle.headingTopMargin.toFixed(1)}em
                </Typography>
                <Slider
                  value={typographyStyle.headingTopMargin}
                  onChange={(_, value) => updateTypographyStyle({ headingTopMargin: value as number })}
                  min={0}
                  max={3.0}
                  step={0.1}
                  size="small"
                />
              </Box>

              {/* Heading Bottom Margin */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Heading Bottom Margin: {typographyStyle.headingBottomMargin.toFixed(1)}em
                </Typography>
                <Slider
                  value={typographyStyle.headingBottomMargin}
                  onChange={(_, value) => updateTypographyStyle({ headingBottomMargin: value as number })}
                  min={0}
                  max={2.0}
                  step={0.1}
                  size="small"
                />
              </Box>

              {/* Section Spacing */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom>
                  Section Spacing: {typographyStyle.sectionSpacing.toFixed(1)}em
                </Typography>
                <Slider
                  value={typographyStyle.sectionSpacing}
                  onChange={(_, value) => updateTypographyStyle({ sectionSpacing: value as number })}
                  min={0.5}
                  max={4.0}
                  step={0.1}
                  size="small"
                />
              </Box>

              {/* Table Borders */}
              <FormControlLabel
                control={
                  <Switch
                    checked={typographyStyle.tableBorders !== false}
                    onChange={(e) => updateTypographyStyle({ tableBorders: e.target.checked })}
                    size="small"
                  />
                }
                label="Show Table Borders"
              />
            </Box>
          </Box>
          )}

          {/* Letterhead Tab */}
          {tabIndex === 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              {/* Company Info Section */}
              <Typography variant="subtitle2" fontWeight="600" color="primary">
                Company Information
              </Typography>

              <TextField
                label="Company Name"
                value={letterheadConfig.companyName}
                onChange={(e) => handleLetterheadChange('companyName', e.target.value)}
                fullWidth
                size="small"
              />

              <TextField
                label="Logo Text (optional)"
                value={letterheadConfig.logoText || ''}
                onChange={(e) => handleLetterheadChange('logoText', e.target.value)}
                fullWidth
                size="small"
                helperText="Override the logo text (e.g., 'wick Apps' instead of 'QwickApps')"
              />

              <TextField
                label="Tagline (optional)"
                value={letterheadConfig.tagline || ''}
                onChange={(e) => handleLetterheadChange('tagline', e.target.value)}
                fullWidth
                size="small"
                helperText="Tagline shown below the logo"
              />

              <TextField
                label="Address"
                value={letterheadConfig.address}
                onChange={(e) => handleLetterheadChange('address', e.target.value)}
                fullWidth
                size="small"
                multiline
                rows={2}
              />

              <TextField
                label="Email"
                value={letterheadConfig.email}
                onChange={(e) => handleLetterheadChange('email', e.target.value)}
                fullWidth
                size="small"
                type="email"
              />

              <TextField
                label="Phone"
                value={letterheadConfig.phone}
                onChange={(e) => handleLetterheadChange('phone', e.target.value)}
                fullWidth
                size="small"
                type="tel"
              />

              <TextField
                label="Website"
                value={letterheadConfig.website}
                onChange={(e) => handleLetterheadChange('website', e.target.value)}
                fullWidth
                size="small"
                type="url"
              />

              <Divider sx={{ my: 1 }} />

              {/* Logo Styling Section */}
              <Typography variant="subtitle2" fontWeight="600" color="primary">
                Logo & Styling
              </Typography>

              <TextField
                label="Custom Logo URL (optional)"
                value={letterheadConfig.logoUrl || ''}
                onChange={(e) => handleLetterheadChange('logoUrl', e.target.value)}
                fullWidth
                size="small"
                type="url"
                helperText="Enter a logo URL or upload an image below"
              />

              <Box>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="logo-upload"
                  type="file"
                  aria-label="Upload Logo Image"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      const reader = new FileReader();
                      reader.onload = (event) => {
                        const dataUrl = event.target?.result as string;
                        handleLetterheadChange('logoUrl', dataUrl);
                      };
                      reader.readAsDataURL(file);
                    }
                  }}
                />
                <label htmlFor="logo-upload">
                  <Button variant="outlined" component="span" size="small" fullWidth>
                    Upload Logo Image
                  </Button>
                </label>
              </Box>

              <FormControl fullWidth size="small">
                <InputLabel>Logo Size</InputLabel>
                <Select
                  value={letterheadConfig.logoSize}
                  label="Logo Size"
                  onChange={(e) => handleLetterheadChange('logoSize', e.target.value)}
                >
                  <MenuItem value="tiny">Tiny</MenuItem>
                  <MenuItem value="small">Small</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="large">Large</MenuItem>
                  <MenuItem value="extra-large">Extra Large</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Logo Variant</InputLabel>
                <Select
                  value={letterheadConfig.logoVariant}
                  label="Logo Variant"
                  onChange={(e) => handleLetterheadChange('logoVariant', e.target.value)}
                >
                  <MenuItem value="default">Default</MenuItem>
                  <MenuItem value="high-contrast">High Contrast</MenuItem>
                  <MenuItem value="monochrome">Monochrome</MenuItem>
                  <MenuItem value="on-primary">On Primary</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Header Alignment</InputLabel>
                <Select
                  value={letterheadConfig.headerAlign || 'left'}
                  label="Header Alignment"
                  onChange={(e) => handleLetterheadChange('headerAlign', e.target.value)}
                >
                  <MenuItem value="left">Left</MenuItem>
                  <MenuItem value="center">Center</MenuItem>
                  <MenuItem value="right">Right</MenuItem>
                </Select>
              </FormControl>

              <Divider sx={{ my: 1 }} />

              {/* Template Customization Section */}
              <Typography variant="subtitle2" fontWeight="600" color="primary">
                Custom Templates (Optional)
              </Typography>

              <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                Use mustache syntax: {`{{logo}}, {{companyName}}, {{tagline}}, {{email}}, {{phone}}, {{website}}, {{address}}, {{date}}`}
              </Typography>

              <TextField
                label="Custom Header Template"
                value={letterheadConfig.headerTemplate || ''}
                onChange={(e) => handleLetterheadChange('headerTemplate', e.target.value)}
                fullWidth
                size="small"
                multiline
                rows={3}
                placeholder={`Example: <div style="display: flex; align-items: center; gap: 16px;">{{logo}}<div><h2>{{companyName}}</h2></div></div>`}
                helperText="Leave empty to use default header layout"
              />

              {letterheadConfig.headerTemplate && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    mt: 1,
                    backgroundColor: 'background.default',
                    borderColor: 'primary.main',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Header Preview:
                  </Typography>
                  {renderTemplatePreview(letterheadConfig.headerTemplate)}
                </Paper>
              )}

              <TextField
                label="Custom Footer Template"
                value={letterheadConfig.footerTemplate || ''}
                onChange={(e) => handleLetterheadChange('footerTemplate', e.target.value)}
                fullWidth
                size="small"
                multiline
                rows={2}
                placeholder={`Example: <div style="text-align: center;">{{companyName}} • {{website}} • {{email}}</div>`}
                helperText="Leave empty to use default footer layout"
                sx={{ mt: 2 }}
              />

              {letterheadConfig.footerTemplate && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1,
                    mt: 1,
                    backgroundColor: 'background.default',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                    Footer Preview:
                  </Typography>
                  {renderTemplatePreview(letterheadConfig.footerTemplate)}
                </Paper>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
